/**
 * Which identifier the fan types back, and whether the typed value matches.
 *
 * An account can have no username and no email (owner answer Q181). `userId`
 * is the only identifier that is always present, so the cascade terminates
 * there. The comparison is case-sensitive on purpose: a lookalike must not
 * unlock the delete button.
 *
 * This file is deliberately import free. It is exposed through the
 * "@sportsmet/ui/logic" subpath and consumed directly by apps/admin, which
 * has no Tailwind and no @sportsmet/theme (admin-ant-design.mdc). Adding an
 * import here, even a React or theme import, would carry that dependency
 * onto admin's import path through this subpath without anything failing
 * loudly, so keep this file free of imports.
 */

export type typeIdentifierSource = "username" | "email" | "memberId";

export type typeAccountIdentifier = {
  /** Lowercase noun phrase naming the field: "username", "email address", "member ID". */
  label: string;
  /** The exact string the fan has to type back. Shown on screen. Never empty. */
  value: string;
  /** Which rung of the cascade won. Lets a test assert the cascade without matching strings. */
  source: typeIdentifierSource;
};

export function resolveAccountIdentifier(input: {
  username: string | null | undefined;
  email: string | null | undefined;
  userId: string;
}): typeAccountIdentifier {
  const username = input.username?.trim() ?? "";
  if (username !== "") {
    return { label: "username", value: username, source: "username" };
  }

  const email = input.email?.trim() ?? "";
  if (email !== "") {
    return { label: "email address", value: email, source: "email" };
  }

  const userId = input.userId.trim();
  if (userId !== "") {
    return { label: "member ID", value: userId, source: "memberId" };
  }

  throw new Error("An account always has a member ID");
}

export function isTypedIdentifierMatch(input: {
  typed: string;
  expected: string;
}): boolean {
  const expected = input.expected.trim();
  if (expected === "") return false;
  return input.typed.trim() === expected;
}

export function buildTypeToConfirmTitle(input: { label: string }): string {
  return `Type your ${input.label} to confirm`;
}

export function buildTypeToConfirmFieldLabel(input: { label: string }): string {
  return `Your ${input.label}`;
}
